// src/lib/sam-api/test.ts
import { samApi } from './client';
import { SamDataValidator } from './validator';
import { supabase } from '../supabase';
import { SamContractResponse } from '../../types/sam';

interface TestResults {
  processed: number;
  successful: number;
  failed: number;
  errors: Array<{
    noticeId: string;
    error: string;
  }>;
}

export class SamApiTest {
  private static readonly SAMPLE_SIZE = 5; // Reduced to 5 for faster testing
  private static readonly logger = console;

  static async runTest(): Promise<TestResults> {
    const results: TestResults = {
      processed: 0,
      successful: 0,
      failed: 0,
      errors: []
    };

    try {
      this.logger.info(`🚀 Starting SAM.gov API test with sample size: ${this.SAMPLE_SIZE}`);
      
      // Test API connection first
      this.logger.info('📡 Testing SAM.gov API connection...');
      const contracts = await this.getSampleContracts();
      results.processed = contracts.length;
      
      this.logger.info(`📋 Retrieved ${contracts.length} contracts from SAM.gov API`);

      // Process each contract
      for (const contract of contracts) {
        try {
          await this.processContract(contract);
          results.successful++;
          this.logger.info(`✅ Successfully processed contract: ${contract.notice_id || contract.noticeId}`);
        } catch (error) {
          results.failed++;
          const noticeId = contract.notice_id || contract.noticeId || 'unknown';
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          results.errors.push({
            noticeId,
            error: errorMessage
          });
          this.logger.error(`❌ Failed to process contract ${noticeId}:`, errorMessage);
        }
      }

      this.logger.info('📊 Test completed:', results);
      return results;
    } catch (error) {
      this.logger.error('💥 Test failed:', error);
      throw error;
    }
  }

  private static async getSampleContracts(): Promise<SamContractResponse[]> {
    try {
      // Use proper date formatting for SAM.gov API (MM/dd/yyyy)
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const formatDate = (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };

      const { items } = await samApi.searchContracts({
        size: this.SAMPLE_SIZE,
        postedFrom: formatDate(thirtyDaysAgo),
        postedTo: formatDate(today)
      });
      
      this.logger.info(`📥 SAM API returned ${items.length} items`);
      return items;
    } catch (error) {
      this.logger.error('❌ Failed to retrieve contracts from SAM.gov API:', error);
      throw new Error(`SAM.gov API connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private static async processContract(contract: SamContractResponse): Promise<void> {
    // Validate contract data
    const validation = SamDataValidator.validateContract(contract);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    // Log warnings but don't fail for them
    if (validation.warnings.length > 0) {
      console.warn(`⚠️ Warnings for contract ${contract.notice_id}:`, validation.warnings);
    }

    // Prepare data for database insertion with proper defaults
    const contractData = {
      notice_id: contract.notice_id,
      title: contract.title,
      agency: contract.agency,
      naics_code: contract.naics_code || contract.naicsCode,
      naics_codes: contract.naicsCodes || [contract.naics_code || contract.naicsCode],
      posted_date: contract.posted_date || contract.postedDate,
      due_date: contract.due_date || contract.dueDate, // This should now always have a value from transformer
      updated_published_date: contract.updated_published_date || contract.updatedPublishedDate || new Date().toISOString(),
      solicitation_number: contract.solicitation_number || contract.solicitationNumber,
      contract_number: contract.contract_number || contract.contractNumber,
      solicitation_url: contract.solicitation_url || contract.solicitationUrl,
      funding_amount: contract.funding_amount || contract.fundingAmount || 0,
      description: contract.description || '',
      status: contract.status || 'pending',
      set_asides: contract.set_asides || contract.setAsides || [],
      place_of_performance: contract.place_of_performance || contract.placeOfPerformance,
      contracting_officer: contract.contracting_officer || contract.contractingOfficer,
      evaluation_criteria: contract.evaluation_criteria || contract.evaluationCriteria,
      required_certifications: contract.required_certifications || contract.requiredCertifications || [],
      required_experience: contract.required_experience || contract.requiredExperience || [],
      organization_type: contract.organization_type || contract.organizationType,
      office_address: contract.office_address || contract.officeAddress,
      point_of_contact: contract.point_of_contact || contract.pointOfContact,
      response_type: contract.response_type || contract.responseType,
      response_instructions: contract.response_instructions || contract.responseInstructions,
      questions_deadline: contract.questions_deadline || contract.questionsDeadline,
      questions_allowed: contract.questions_allowed || contract.questionsAllowed || false,
      psc_code: contract.psc_code || contract.pscCode,
      award_number: contract.award_number || contract.awardNumber,
      award_date: contract.award_date || contract.awardDate,
      award_amount: contract.award_amount || contract.awardAmount || 0,
      base_period_amount: contract.base_period_amount || contract.basePeriodAmount || 0,
      option_periods: contract.option_periods || contract.optionPeriods,
      total_contract_value: contract.total_contract_value || contract.totalContractValue || 0,
      active: contract.active !== undefined ? contract.active : true,
      archived: contract.archived || false,
      metadata: contract.metadata || { source: 'sam.gov', imported_at: new Date().toISOString() }
    };

    // Final validation for storage
    const storageValidation = SamDataValidator.validateContractForStorage(contractData);
    if (!storageValidation.isValid) {
      console.error(`❌ Storage validation failed for ${contract.notice_id}:`, storageValidation.errors);
      console.error('Contract data:', contractData);
      throw new Error(`Storage validation failed: ${storageValidation.errors.join(', ')}`);
    }

    // Store in database with conflict resolution
    const { error: upsertError } = await supabase
      .from('sam_contracts')
      .upsert(contractData, {
        onConflict: 'notice_id'
      });

    if (upsertError) {
      throw new Error(`Database upsert failed: ${upsertError.message}`);
    }

    // Store any associated documents if present
    if (contract.documents && contract.documents.length > 0) {
      const documentData = contract.documents.map(doc => ({
        notice_id: contract.notice_id,
        name: doc.name || doc.title,
        type: doc.type,
        storage_path: '',
        file_url: doc.url,
        date_posted: doc.datePosted,
        metadata: { original_doc: doc }
      }));

      const { error: docError } = await supabase
        .from('sam_documents')
        .upsert(documentData, {
          onConflict: 'notice_id,file_url'
        });

      if (docError) {
        this.logger.warn(`Failed to store documents for ${contract.notice_id}:`, docError.message);
      }
    }
  }

  static generateReport(results: TestResults): string {
    const successRate = results.processed > 0 ? ((results.successful / results.processed) * 100).toFixed(2) : '0.00';
    
    return `
SAM.gov API Integration Test Report
==================================

Summary:
--------
Total Processed: ${results.processed}
Successful: ${results.successful}
Failed: ${results.failed}
Success Rate: ${successRate}%

${results.errors.length > 0 ? `
Errors:
-------
${results.errors.map(err => `❌ ${err.noticeId}: ${err.error}`).join('\n')}
` : '✅ No errors encountered!'}

${results.successful > 0 ? `
✅ Successfully imported ${results.successful} contracts into the database.
` : ''}

Test completed at: ${new Date().toISOString()}
    `.trim();
  }

  // Helper method to run a quick connectivity test
  static async testConnectivity(): Promise<boolean> {
    try {
      this.logger.info('🔍 Testing SAM.gov API connectivity...');
      
      // Use proper date formatting for connectivity test
      const today = new Date();
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const formatDate = (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };

      const { items } = await samApi.searchContracts({ 
        size: 1,
        postedFrom: formatDate(sevenDaysAgo),
        postedTo: formatDate(today)
      });
      
      const isConnected = Array.isArray(items);
      this.logger.info(`📡 SAM.gov API connectivity: ${isConnected ? '✅ SUCCESS' : '❌ FAILED'}`);
      return isConnected;
    } catch (error) {
      this.logger.error('❌ SAM.gov API connectivity test failed:', error);
      return false;
    }
  }

  // Helper method to test database connectivity
  static async testDatabaseConnectivity(): Promise<boolean> {
    try {
      this.logger.info('🗄️ Testing database connectivity...');
      const { data, error } = await supabase
        .from('sam_contracts')
        .select('notice_id')
        .limit(1);
      
      if (error) throw error;
      
      this.logger.info('✅ Database connectivity: SUCCESS');
      return true;
    } catch (error) {
      this.logger.error('❌ Database connectivity test failed:', error);
      return false;
    }
  }
}